import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("success");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (err) {
        console.error("Payment status poll failed, will retry:", err);
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <CircleNotch size={56} weight="bold" color="var(--bk-accent)" className="mx-auto animate-spin" />
            <h1 className="font-display text-3xl font-bold mt-6" style={{ color: "var(--bk-text)" }}>Confirming your payment…</h1>
            <p className="text-base mt-2" style={{ color: "var(--bk-text-2)" }}>This only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle size={64} weight="fill" color="var(--bk-success)" className="mx-auto" data-testid="payment-success-icon" />
            <h1 className="font-display text-4xl font-bold mt-6" style={{ color: "var(--bk-text)" }}>You're upgraded!</h1>
            <p className="text-base mt-3" style={{ color: "var(--bk-text-2)" }}>Your plan is active — you can now add more clients and keep their books in one place.</p>
            <button onClick={() => navigate("/dashboard")} data-testid="go-dashboard" className="bk-btn-primary px-7 py-3 rounded-md text-sm font-semibold mt-8">
              Go to dashboard
            </button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle size={64} weight="fill" color="#B23B1F" className="mx-auto" />
            <h1 className="font-display text-3xl font-bold mt-6" style={{ color: "var(--bk-text)" }}>
              {state === "timeout" ? "Still processing…" : "Payment not completed"}
            </h1>
            <p className="text-base mt-3" style={{ color: "var(--bk-text-2)" }}>
              {state === "timeout"
                ? "Your payment is taking longer than expected. Refresh in a minute or check your dashboard."
                : "We couldn't confirm your payment. You can try again from the pricing page."}
            </p>
            <div className="flex items-center justify-center gap-3 mt-8">
              <Link to="/dashboard" className="px-6 py-3 rounded-md text-sm font-semibold border" style={{ borderColor: "var(--bk-text)", color: "var(--bk-text)" }}>Dashboard</Link>
              <Link to="/pricing" className="bk-btn-primary px-6 py-3 rounded-md text-sm font-semibold">Back to pricing</Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
